import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  FormControl,
  Chip,
  Stack,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Question from "../../components/Question";
import ContinueButton from "../../components/ContinueButton";
import { continueButtonSx, inputLabelSx, textAreaSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import { setAccutaneSideEffects as updateSideEffects } from "../../redux/userSlice";

const SIDE_EFFECT_OPTIONS = [
  "New pimples",
  "Dry lips",
  "Sunburns",
  "Belly pain",
  "Bloody stools",
  "Joint pain",
  "Vision changes",
  "New medications",
  "Drinking alcohol",
  "Mood changes",
  "Depression",
  "Loss of interest in normal activities",
  "Thoughts of hurting yourself",
  "Not listed. Please tell me more",
];

const MENTAL_HEALTH_FLAGS = new Set([
  "Mood changes",
  "Depression",
  "Loss of interest in normal activities",
  "Thoughts of hurting yourself",
]);

const OTHER_LABEL = "Not listed. Please tell me more";

export default function SideEffectsChecklist() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const user = useSelector((state) => state.user);

  // Use your accutane-specific slice key, not user.sideEffects
  const [selected, setSelected] = useState(
    Array.isArray(user.accutaneSideEffects?.selected) ? user.accutaneSideEffects.selected : []
  );
  const [otherText, setOtherText] = useState(user.accutaneSideEffects?.otherText || "");

  useEffect(() => {
    if (Array.isArray(user.accutaneSideEffects?.selected)) {
      setSelected(user.accutaneSideEffects.selected);
    }
    if (user.accutaneSideEffects?.otherText) {
      setOtherText(user.accutaneSideEffects.otherText);
    }
  }, [user.accutaneSideEffects?.selected, user.accutaneSideEffects?.otherText]);

  const showOther = selected.includes(OTHER_LABEL);

  const hasMentalHealthConcern = useMemo(
    () => selected.some((x) => MENTAL_HEALTH_FLAGS.has(x)),
    [selected]
  );

  const toggle = (label) => {
    setSelected((prev) => {
      const isSelected = prev.includes(label);
      const next = isSelected ? prev.filter((x) => x !== label) : [...prev, label];

      // If "Other" unchecked, clear free text
      if (!next.includes(OTHER_LABEL)) {
        setOtherText("");
      }
      return next;
    });
  };

  const disableContinue = showOther && otherText.trim() === "";

  const handleNext = () => {
    dispatch(
      updateSideEffects({
        selected,
        otherText: showOther ? otherText.trim() : undefined,
      })
    );

    if (hasMentalHealthConcern) {
      setPageAndDeferNavigate(dispatch, "/phq9-questionnaire", () =>
        navigate("/phq9-questionnaire", { state: { returning: true } })
      );
      return;
    }
    if (
        user.sex?.sexAtBirth === "Female" ||
        user.sex?.sexAtBirth === "Prefer not to answer"
    ) {
        setPageAndDeferNavigate(dispatch, '/maternity', () =>
            navigate('/maternity', { state: { returning: true } })
        );
    } else {
        setPageAndDeferNavigate(dispatch, '/recent-photo', () =>
            navigate('/recent-photo', { state: { returning: true } })
        );
    }
  };

  const handleBack = () => {
    setPageAndDeferNavigate(dispatch, "/missed-doses", navigate);
  };

  return (
    <Question
      onBack={handleBack}
      title="Are you experiencing any of the following side effects?"
      subtitle="Select any that apply."
      input={
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControl sx={{ width: "100%", mb: "1rem" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: isDesktop ? "900px" : "100%",
                mx: "auto",
                mt: 2,
              }}
            >
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {SIDE_EFFECT_OPTIONS.map((label) => {
                  const isSelected = selected.includes(label);

                  return (
                    <Chip
                      key={label}
                      label={label}
                      clickable
                      onClick={() => toggle(label)}
                      variant={isSelected ? "filled" : "outlined"}
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: isSelected ? 600 : 400,
                        backgroundColor: isSelected ? "#82B1FF33" : "transparent",
                        color: isSelected
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                        borderColor: isSelected
                          ? theme.palette.primary.main
                          : theme.palette.grey[400],
                        height: "auto",
                        alignItems: "flex-start",
                        maxWidth: isDesktop ? "420px" : "100%",

                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          lineHeight: 1.2,
                          paddingTop: "6px",
                          paddingBottom: "6px",
                        },

                        "&:hover": {
                          backgroundColor: isSelected
                            ? "#82B1FF33"
                            : theme.palette.action.hover,
                        },
                      }}
                    />
                  );
                })}
              </Stack>

              {showOther && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    label="Please tell us more"
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    multiline
                    rows={2}
                    sx={{
                      ...textAreaSx,
                      "& .MuiInputLabel-root": {
                        backgroundColor: theme.palette.background.paper,
                        padding: "0 32px",
                        transition: "all 0.2s ease-in-out",
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        ...inputLabelSx,
                        "& .MuiInputLabel-shrink": {
                          backgroundColor: theme.palette.background.paper,
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </FormControl>
        </Box>
      }
      footer={
        <Box
          sx={{
            ...continueButtonSx,
            width: "100%",
            maxWidth: "900px",
            margin: "0 auto",
            position: "sticky",
            bottom: 0,
          }}
        >
          <ContinueButton
            onClick={handleNext}
            title={hasMentalHealthConcern ? "Continue" : "Continue"}
            disabled={disableContinue}
            selected={!disableContinue}
          />
        </Box>
      }
    />
  );
}
